import { useMemo, useState } from 'react';
import { Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DataTable, EmptyState, Pagination, TimeRangePicker, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Card,
  CardBody,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useMyReceivedRatings,
  useAwaitingRatings,
  type InternalRating,
  type AwaitingRatingItem,
  type ReceivedRatingFilters,
} from '@/api/internal-ratings';
import { formatDate, formatDateTime } from '@/lib/format';
import { paginationView } from '@/pages/customers/pagination-summary';
import { RatingSummaryCard } from './rating-summary-card';

const PAGE_SIZE = 20;

/* ── Received ratings tab ───────────────────────────────────────────────── */

export function ReceivedTab() {
  const [page, setPage] = useState(1);
  const [raterSearch, setRaterSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [star, setStar] = useState<number | undefined>(undefined);
  const [withComment, setWithComment] = useState<boolean | undefined>(undefined);

  const filters: ReceivedRatingFilters = useMemo(() => ({
    ...(raterSearch.trim() ? { rater_search: raterSearch.trim() } : {}),
    ...(fromDate ? { from_date: fromDate } : {}),
    ...(toDate ? { to_date: toDate } : {}),
    ...(star !== undefined ? { star } : {}),
    ...(withComment !== undefined ? { with_comment: withComment } : {}),
  }), [raterSearch, fromDate, toDate, star, withComment]);

  const hasFilters = !!(raterSearch || fromDate || toDate || star !== undefined || withComment !== undefined);

  const handleReset = () => {
    setRaterSearch('');
    setFromDate('');
    setToDate('');
    setStar(undefined);
    setWithComment(undefined);
    setPage(1);
  };

  const handleStarFilter = (s: number) => {
    setStar((prev) => (prev === s ? undefined : s));
    setPage(1);
  };

  const query = useMyReceivedRatings(page, PAGE_SIZE, filters);
  const items = query.data?.items ?? [];
  const hasMore = query.data?.has_more ?? false;
  const summary = query.data?.summary;

  const pageCount = hasMore ? page + 1 : page;
  const { summary: footerSummary } = paginationView(
    page, PAGE_SIZE, items.length, page * PAGE_SIZE, pageCount,
  );

  const columns = useMemo<ColumnDef<InternalRating, unknown>[]>(
    () => [
      {
        accessorKey: 'entity_type_label',
        header: 'Type',
        cell: ({ row }) => row.original.entity_type_label ?? row.original.entity_type,
      },
      {
        accessorKey: 'entity_id',
        header: 'Ref',
        cell: ({ row }) => {
          const id = row.original.entity_id;
          const title = row.original.entity_title;
          return title ? `#${id} — ${title}` : `#${id}`;
        },
      },
      {
        accessorKey: 'rating',
        header: 'Stars',
        cell: ({ getValue }) => {
          const val = getValue() as number;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', color: 'var(--vn-warning-solid)' }}>
              <Star size={13} />
              {val}
            </span>
          );
        },
      },
      { accessorKey: 'comment', header: 'Comment' },
      {
        accessorKey: 'rater_employee_name',
        header: 'Rated by',
        cell: ({ row }) => {
          const name = row.original.rater_employee_name;
          const code = row.original.rater_employee_code;
          return name ? (code ? `${name} (${code})` : name) : '—';
        },
      },
      {
        accessorKey: 'rated_employee_names',
        header: 'Co-rated',
        cell: ({ getValue }) => {
          const names = getValue() as string[] | undefined;
          return names && names.length > 0 ? names.join(', ') : '—';
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Date',
        cell: ({ getValue }) => formatDateTime(getValue() as string),
      },
    ],
    [],
  );

  if (query.isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load ratings"
        actions={<Button size="sm" variant="ghost" onClick={() => query.refetch()}>Retry</Button>}
      />
    );
  }

  if (query.isLoading) {
    return (
      <Card flat>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} style={{ height: 24 }} />)}
        </CardBody>
      </Card>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {summary && (
        <RatingSummaryCard
          summary={summary}
          activeStar={star}
          onStarFilter={handleStarFilter}
        />
      )}

      {/* Filters row */}
      <Card flat>
        <CardBody>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-3)', alignItems: 'flex-end' }}>
            <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
              <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Rater search</span>
              <Input
                value={raterSearch}
                onChange={(e) => { setRaterSearch(e.target.value); setPage(1); }}
                placeholder="Name or code…"
                style={{ maxWidth: 200 }}
              />
            </div>
            <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
              <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Period</span>
              <TimeRangePicker
                showLive={false}
                presets={[]}
                from={fromDate}
                to={toDate}
                triggerLabel={fromDate && toDate ? `${fromDate} → ${toDate}` : 'Select dates'}
                onApply={(v) => {
                  if (v.from && v.to) {
                    setFromDate(v.from.slice(0, 10));
                    setToDate(v.to.slice(0, 10));
                    setPage(1);
                  }
                }}
              />
            </div>
            <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
              <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Stars</span>
              <Select
                value={star !== undefined ? String(star) : 'all'}
                onValueChange={(v) => { setStar(v === 'all' ? undefined : Number(v)); setPage(1); }}
              >
                <SelectTrigger style={{ minWidth: 100 }}>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {[5, 4, 3, 2, 1].map((s) => (
                    <SelectItem key={s} value={String(s)}>{s} star{s !== 1 ? 's' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', paddingBottom: 2 }}>
              <Switch
                checked={withComment === true}
                onCheckedChange={(checked) => {
                  setWithComment(checked ? true : undefined);
                  setPage(1);
                }}
              />
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Has comment</span>
            </div>
            {hasFilters && (
              <Button size="sm" variant="secondary" onClick={handleReset} style={{ alignSelf: 'flex-end' }}>
                Reset
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      <DataTable
        columns={columns}
        data={items}
        getRowId={(r) => String(r.rating_id)}
        emptyState={<EmptyState title="No ratings received yet" />}
        footer={
          <Pagination page={page} pageCount={pageCount} onPageChange={setPage} summary={footerSummary} />
        }
      />
    </div>
  );
}

/* ── Awaiting ratings tab ───────────────────────────────────────────────── */

export function AwaitingTab() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [ticketType, setTicketType] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);

  const query = useAwaitingRatings(page, PAGE_SIZE);
  const allItems = query.data?.items ?? [];

  // Client-side filters (awaiting endpoint doesn't expose server-side filter params)
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    return allItems.filter((r) => {
      if (q && !r.ticket_content.toLowerCase().includes(q) && !String(r.ticket_id).includes(q)) return false;
      if (ticketType && !r.ticket_type.toLowerCase().includes(ticketType.toLowerCase())) return false;
      if (overdueOnly && r.rating_due_at && new Date(r.rating_due_at).getTime() >= now) return false;
      return true;
    });
  }, [allItems, search, ticketType, overdueOnly]);

  const hasMore = query.data?.has_more ?? false;
  const pageCount = hasMore ? page + 1 : page;
  const { summary: footerSummary } = paginationView(
    page, PAGE_SIZE, items.length, page * PAGE_SIZE, pageCount,
  );

  // Unique ticket types from current page for the filter selector
  const ticketTypes = useMemo(() => {
    const types = new Set(allItems.map((r) => r.ticket_type));
    return Array.from(types).sort();
  }, [allItems]);

  const hasFilters = !!(search || ticketType || overdueOnly);

  const handleReset = () => {
    setSearch('');
    setTicketType('');
    setOverdueOnly(false);
    setPage(1);
  };

  const columns = useMemo<ColumnDef<AwaitingRatingItem, unknown>[]>(
    () => [
      {
        accessorKey: 'ticket_id',
        header: 'Ticket',
        cell: ({ getValue }) => `#${getValue()}`,
      },
      { accessorKey: 'ticket_content', header: 'Content' },
      { accessorKey: 'ticket_type', header: 'Type' },
      {
        accessorKey: 'rated_department_names',
        header: 'Rate for',
        cell: ({ getValue }) => {
          const names = getValue() as string[];
          return names && names.length > 0 ? names.join(', ') : '—';
        },
      },
      {
        accessorKey: 'rated_employee_names',
        header: 'Employees',
        cell: ({ getValue }) => {
          const names = getValue() as string[] | undefined;
          return names && names.length > 0 ? names.join(', ') : '—';
        },
      },
      {
        accessorKey: 'completed_at',
        header: 'Completed',
        cell: ({ getValue }) => formatDate(getValue() as string),
      },
      {
        accessorKey: 'rating_due_at',
        header: 'Due',
        cell: ({ getValue }) => formatDate(getValue() as string | null | undefined),
      },
    ],
    [],
  );

  if (query.isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load awaiting ratings"
        actions={<Button size="sm" variant="ghost" onClick={() => query.refetch()}>Retry</Button>}
      />
    );
  }

  if (query.isLoading) {
    return (
      <Card flat>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} style={{ height: 24 }} />)}
        </CardBody>
      </Card>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Filters row */}
      <Card flat>
        <CardBody>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-3)', alignItems: 'flex-end' }}>
            <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
              <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Search</span>
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Ticket ID or content…"
                style={{ maxWidth: 240 }}
              />
            </div>
            {ticketTypes.length > 0 && (
              <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
                <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Ticket type</span>
                <Select
                  value={ticketType || 'all'}
                  onValueChange={(v) => { setTicketType(v === 'all' ? '' : v); setPage(1); }}
                >
                  <SelectTrigger style={{ minWidth: 180 }}>
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {ticketTypes.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', paddingBottom: 2 }}>
              <Switch
                checked={overdueOnly}
                onCheckedChange={(checked) => { setOverdueOnly(checked); setPage(1); }}
              />
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Overdue only</span>
            </div>
            {hasFilters && (
              <Button size="sm" variant="secondary" onClick={handleReset} style={{ alignSelf: 'flex-end' }}>
                Reset
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      <DataTable
        columns={columns}
        data={items}
        getRowId={(r) => String(r.ticket_id)}
        onRowClick={(row) => navigate(`/tickets/${row.ticket_id}?rate=1`)}
        emptyState={<EmptyState title="No tickets awaiting your rating" />}
        footer={
          <Pagination page={page} pageCount={pageCount} onPageChange={setPage} summary={footerSummary} />
        }
      />
    </div>
  );
}
